import React from 'react';

export const sceneNineMeta = {
  id: 'scene-nine-tree-to-forest',
  title: '場景九：一棵樹到一片森林',
  excerpt:
    '留下的不是財富，而是一套能持續成長的系統；真正的傳承，是讓下一代學會種樹的方法。',
};

const SceneNineTreeToForest: React.FC = () => {
  return (
    <article className="forest-article-detail">
      <h2>一棵樹到一片森林</h2>
      <h3>留下的不是財富，而是一套持續成長的系統</h3>

      <p>我常有一個念頭。</p>
      <p>不一定要留很多遺產給孩子。</p>
      <p>真正重要的，是留下能夠持續成長的系統。</p>
      <p>
        如果留下一棵會持續成長的樹，例如優質企業股權、ETF、BRK.B 等長期資產，
        時間就可能把一棵樹，慢慢長成一片森林。
      </p>
      <p>然而，這篇文章真正想談的，其實不是投資，而是人生。</p>

      <h3>一、人生就是種樹</h3>
      <p>森林裡有個最簡單的真理：</p>
      <blockquote>種瓜得瓜，種豆得豆。</blockquote>
      <p>今天種下什麼，未來就收穫什麼。</p>
      <p>人生也是如此。</p>
      <p>選擇工作或創業，結果不同。</p>
      <p>選擇什麼朋友，結果不同。</p>
      <p>選擇什麼伴侶，結果不同。</p>
      <p>選擇什麼價值觀，結果也不同。</p>
      <p>投資亦然。</p>
      <p>買進沒有競爭力的公司，十年後可能負報酬。</p>
      <p>買進優質企業，十年後可能成長數倍。</p>
      <p>選股其實就是選樹種，人生也是。</p>

      <h3>二、選擇比努力更重要</h3>
      <p>很多人努力澆水，卻忘了先看自己種的是什麼樹。</p>
      <p>森林告訴我們：努力很重要，但方向更重要。</p>
      <p>選錯種子，努力十年，可能仍然一無所獲。</p>
      <p>選對種子，時間會替你工作。</p>
      <blockquote>選擇決定方向，時間決定結果。</blockquote>
      <p>這就是機會成本，也是時間價值。</p>

      <h3>三、紅杉資本的啟示</h3>
      <p>世界最成功的創投公司之一：Sequoia Capital，名字來自加州紅杉樹。</p>
      <p>Coast Redwood 紅杉樹可以活超過兩千年，高達一百公尺以上。</p>
      <p>歷經風雨，依然屹立不搖。</p>
      <p>
        紅杉資本創辦人希望：不要追逐短期收成，而是尋找未來能長成參天巨木的種子。
      </p>
      <p>因此他們投資的，不是今天最大的公司，而是未來可能成為森林的幼苗。</p>
      <p>這不只是投資哲學，更是人生哲學。</p>

      <h3>四、巴菲特談傳承</h3>
      <p>投資大師 Warren Buffett 曾說：</p>
      <blockquote>
        給孩子足夠的資源，讓他們可以做任何事；但不要給到讓他們什麼事都不用做。
      </blockquote>
      <p>真正的愛，不是替孩子走完人生。</p>
      <p>而是給他一塊土地，讓他學會自己種樹。</p>

      <h3>五、留遺產，不如留系統</h3>
      <p>我愈來愈相信：財富不是根，系統才是根。</p>
      <p>真正值得傳承的，不是金錢，而是：</p>
      <ul>
        <li>長期投資系統</li>
        <li>財務教育系統</li>
        <li>家族信託系統</li>
        <li>終身學習系統</li>
        <li>健康管理系統</li>
        <li>品格養成系統</li>
      </ul>
      <p>因為財富只是果實，系統才是樹根。</p>
      <blockquote>不留下很多錢，留下一套會持續成長的系統。</blockquote>

      <h3>六、如何讓種樹這件事不中斷？</h3>
      <p>森林裡有個現象。</p>
      <p>許多人種樹失敗，不是因為樹種不好，而是在樹還沒長大之前，就把它砍掉了。</p>
      <p>投資也是如此。</p>
      <p>許多人不是輸給市場，而是輸給自己的情緒。</p>
      <p>於是我開始思考：</p>
      <p>如何讓種樹這件事，即使我離開世界之後，仍然能繼續進行？</p>
      <p>我的答案是：建立制度，建立信託，建立規則。</p>
      <p>讓種樹這件事，不依賴人的情緒，而依賴制度運作。</p>

      <h3>七、我的理想：讓森林長大</h3>
      <p>我理想中的做法是：</p>
      <p>透過家族信託，將部分資產投入優質企業與長期資產。</p>
      <p>例如：</p>
      <ul>
        <li>ETF</li>
        <li>優質企業股權</li>
        <li>BRK.B 等長期成長資產</li>
      </ul>
      <p>並設定規則：本金不得動用，持續再投資，讓樹不斷長大。</p>
      <p>例如：資產進入信託後，持續成長五十年。</p>
      <p>五十年後，子孫才能開始領取部分獲利，例如每年提領總資產的 5%。</p>
      <p>而本金依然留在森林裡，繼續成長。</p>
      <p>如此領到的，不是一次性的遺產，而是一條永續流動的河流。</p>

      <h3>八、真正傳承的是時間</h3>
      <p>很多人以為財富來自金錢。</p>
      <p>其實財富真正的來源是：時間。</p>
      <p>一百萬元，今天花掉就沒了。</p>
      <p>但如果種成一棵樹，經過數十年，可能變成一整座森林。</p>
      <p>因此我想傳承給下一代的，不是某一筆錢，而是時間複利的力量。</p>

      <h3>九、我真正想留下的是什麼？</h3>
      <p>我真正想留下的，不是森林，而是種樹的方法。</p>
      <p>不是財富，而是創造財富的能力。</p>
      <p>不是結果，而是能持續創造結果的生命系統。</p>
      <p>若後代具備：</p>
      <ul>
        <li>長期投資的智慧</li>
        <li>誠信做人的品格</li>
        <li>持續學習的能力</li>
        <li>面對逆境的勇氣</li>
      </ul>
      <p>即使森林遭遇風雨，仍然能長出新的森林。</p>
      <p>所以我只負責種樹，不執著於森林。</p>
      <p>我只負責改變因緣，不執著於結果。</p>
      <p>因為我相信：</p>
      <blockquote>
        種瓜得瓜，種豆得豆。選擇決定方向，時間決定結果。留下財富，不如留下系統。
        留下森林，不如留下種樹的方法。
      </blockquote>
      <p>一棵樹，終將決定一片森林。</p>
    </article>
  );
};

export default SceneNineTreeToForest;
